import React from "react";

import { useFormContext } from "react-hook-form";

import { EnvironmentSelect } from "src/baseConnectionNode/EnvironmentSelect";
import {
  ConnectionDataSources,
  ConnectionDataSourcesValues,
} from "src/baseConnectionNode/types";
import { Pill } from "src/design-system/Pill";
import { useIsIcebergLongTestRunsEnabled } from "src/hooks/useIsIcebergLongTestRunsEnabled";

const ENV_DOCS_LINK =
  "https://docs.taktile.com/agents/using-agents-in-decision-flows#configure-the-agent-node";

type AvailableSources =
  | ConnectionDataSourcesValues.mock_data
  | ConnectionDataSourcesValues.production;

const AGENT_OPTIONS_LABEL_MAP: Record<AvailableSources, string> = {
  [ConnectionDataSourcesValues.mock_data]: "Mock data",
  [ConnectionDataSourcesValues.production]: "Agent response",
} as const;

const AGENT_RESPONSE_OPTION = {
  key: ConnectionDataSourcesValues.production,
  value: ConnectionDataSourcesValues.production,
} as const;

const MOCK_DATA_OPTION = {
  key: ConnectionDataSourcesValues.mock_data,
  value: ConnectionDataSourcesValues.mock_data,
} as const;

const getEnvironmentOptionLabel = (value: AvailableSources | undefined) => {
  if (!value) return undefined;

  return AGENT_OPTIONS_LABEL_MAP[value];
};

type AgentEnvironmentConfigProps = {
  disabled: boolean;
};

export const AgentEnvironmentConfig: React.FC<AgentEnvironmentConfigProps> = ({
  disabled,
}) => {
  const { watch } = useFormContext();
  const isIcebergLongTestRunsEnabled = useIsIcebergLongTestRunsEnabled();

  const currentTestRunValue: AvailableSources | undefined = watch(
    `config.environments_config.${ConnectionDataSources.authoring_mode_data_source}`,
  );

  const showAgentResponseInTestRuns =
    isIcebergLongTestRunsEnabled ||
    currentTestRunValue === ConnectionDataSourcesValues.production;

  const testRunOptions = showAgentResponseInTestRuns
    ? [MOCK_DATA_OPTION, AGENT_RESPONSE_OPTION]
    : [MOCK_DATA_OPTION];

  const testRunOptionsDisabled = !showAgentResponseInTestRuns;

  const commonProps = {
    dataLocPrefix: "agent-node",
    docsLink: ENV_DOCS_LINK,
    getOptionLabel: getEnvironmentOptionLabel,
  };

  return (
    <div className="flex flex-col flex-wrap">
      <EnvironmentSelect
        defaultOption={MOCK_DATA_OPTION["key"]}
        description="Test runs using the button in the UI"
        disabled={testRunOptionsDisabled || disabled}
        environment={ConnectionDataSources.authoring_mode_data_source}
        options={testRunOptions}
        title="For test runs, use"
        tooltipTitle={
          testRunOptionsDisabled
            ? "Agent response isn't available for test runs yet. To test your agent, use the /sandbox/decide endpoint."
            : undefined
        }
        {...commonProps}
      />
      <EnvironmentSelect
        defaultOption={MOCK_DATA_OPTION["key"]}
        description={
          <>
            API calls using the{" "}
            <Pill size="sm" variant="gray">
              <Pill.Text>/sandbox/decide</Pill.Text>
            </Pill>{" "}
            endpoint
          </>
        }
        disabled={disabled}
        environment={ConnectionDataSources.sandbox_mode_data_source}
        options={[MOCK_DATA_OPTION, AGENT_RESPONSE_OPTION]}
        title="For sandbox API calls, use"
        {...commonProps}
      />
      {/* The live setting remains the same no matter what */}
      <EnvironmentSelect
        defaultOption={AGENT_RESPONSE_OPTION["key"]}
        description={
          <>
            Production API calls using the{" "}
            <Pill size="sm" variant="gray">
              <Pill.Text>/decide</Pill.Text>
            </Pill>{" "}
            endpoint
          </>
        }
        environment={ConnectionDataSources.live}
        options={[AGENT_RESPONSE_OPTION]}
        title="For live API calls, use"
        tooltipTitle="Live API calls always invoke your production agent."
        disabled
        {...commonProps}
      />
    </div>
  );
};
